import { FieldValues, useController } from 'react-hook-form';
import type { GetOptionValue, GroupBase } from 'react-select';
import { Combobox, useAwaitableItems } from '@spark-web/combobox';
import { Field } from '@spark-web/field';
import type { ComboboxFieldProps, Nullable } from './types';

function isGroupItem<TItem>(item: TItem | GroupBase<TItem>): item is GroupBase<TItem> {
  return (item as GroupBase<TItem>).options !== undefined;
}

function ComboboxField<TFieldValues extends FieldValues = FieldValues, TItem = unknown>({
  name,
  label,
  control,
  shouldUnregister,
  FieldProps,
  getOptionValue: _getOptionValue,
  items: _items,
  isLoading,
  comboboxContainerRef,
  ...comboboxProps
}: ComboboxFieldProps<TFieldValues, TItem>) {
  const {
    field: { ref, onChange, value: _value, ...field },
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    shouldUnregister,
  });

  const { items, loading } = useAwaitableItems(_items);

  /**
   * Flatten group items to be used for finding selected item
   */
  const flattenItems = items.flatMap(item => (isGroupItem(item) ? (item as GroupBase<TItem>)?.options : item));

  /**
   * Get the selected item from react-hook-form value
   * The value from react-hook-form can be of any type, but react-select always expects TItem
   */
  const selectedItem =
    flattenItems.find(item => {
      if (_getOptionValue) {
        return _getOptionValue(item) === _value;
      }

      return item === _value;
    }) ?? null;

  /**
   * Implement react-select builtin function to cater for custom value type
   * @param option
   * @returns string
   */
  const getOptionValue: GetOptionValue<TItem> = (option: TItem) => {
    const value = _getOptionValue?.(option);

    // * The value can be of any type, but react-select builtin getOptionValue always returns a string
    return `${value}`;
  };

  const handleChange = (newItem: Nullable<TItem>) => {
    onChange(_getOptionValue && newItem ? _getOptionValue(newItem) : newItem);
    comboboxProps?.onChange?.(newItem);
  };

  return (
    <Field
      label={label}
      {...FieldProps}
      tone={error ? 'critical' : FieldProps?.tone}
      message={error?.message ?? FieldProps?.message}
    >
      <Combobox<TItem>
        {...comboboxProps}
        {...field}
        items={items}
        value={selectedItem}
        getOptionValue={getOptionValue}
        isLoading={isLoading ?? loading}
        onChange={handleChange}
        {...(comboboxContainerRef && {
          menuPortalTarget: document.getElementById(comboboxContainerRef),
        })}
      />
    </Field>
  );
}

export default ComboboxField;
